/**
 * The zen dialog — a modal that looks like the chat window
 * (`features/chat/inbox-chat-modal.tsx`), because it is the same kind of
 * thing: a full-attention reading surface you open on top of a page, not a
 * small confirmation box.
 *
 * WHY THIS EXISTS RATHER THAN `DialogContent`: the design-system dialog
 * renders its own overlay with fixed classes (`bg-black/10`,
 * `backdrop-blur-xs`) and centres a small `sm:max-w-sm` popover. The chat
 * window's surface is a different register — a dimmed, properly blurred
 * backdrop with a single tall card, an 18px radius, the large shadow, and a
 * quiet eyebrow bar carrying the EDDY mark. Matching that from the outside
 * is not possible, so this composes the Radix primitives directly (the same
 * import `components/ui/dialog.tsx` itself uses) and keeps Radix's focus
 * trap, Escape handling and aria wiring.
 *
 * WHY IT IS NOT THE CHAT MODAL: `InboxChatModal` is a hand-rolled overlay,
 * and CLAUDE.md's "modal viewers are mutually exclusive, never stacked"
 * rule is about that specific component — a surface that opens it from
 * inside another dialog closes that dialog in the same click. Nothing here
 * opens it; a row inside this dialog is a plain route `Link`, exactly as
 * the skill result/failure dialogs already do.
 *
 * The numbers below (backdrop tint and blur, 56px/24px inset, radius,
 * shadow, the two keyframes) were copied deliberately from the chat window
 * so the two surfaces read as one family. FRONT-2379 then restyled the chat
 * window's own eyebrow into Builder's wordmark-crumb shape (`EddyWordmark` +
 * "/" + title, instead of the small "E" tile + "EDDY · <context>" line this
 * file's `ZenDialogHeader` still renders below) — deliberately NOT mirrored
 * here, so this dialog is currently the one surface still carrying the
 * older eyebrow shape the numbers above were copied from.
 */

import * as React from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { X } from '@phosphor-icons/react';
import { EddyMark } from '~/components/eddy-mark';
import { cn } from '~/lib/utils';

export function ZenDialog({
  open,
  onOpenChange,
  children,
  className,
  maxWidth = 1040,
  'data-testid': testId,
  onEscapeKeyDown,
  onOpenAutoFocus,
  onCloseAutoFocus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
  /** Panel width ceiling in px. The chat window uses a reader-friendly
   *  column; a listing wants more room, so this is a prop rather than a
   *  constant. */
  maxWidth?: number;
  'data-testid'?: string;
  /**
   * FRONT-2423 lap 2 review (M4): opt-in escape interception, forwarded
   * straight to Radix's `Dialog.Content`. Omitted (the default for every
   * other caller) leaves Radix's own Escape-closes behaviour untouched —
   * this exists so the task window can call `event.preventDefault()` when
   * an inline field has focus, so Escape cancels THAT field instead of
   * closing the whole window, without changing anyone else's dialog.
   */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  /**
   * FRONT-2423 lap 2 (bug 2 fix): opt-in override of Radix's default
   * open-focus search, forwarded straight to `Dialog.Content`. Radix's
   * default focuses the first TABBABLE descendant, which for the task
   * window is a header icon button wrapped in `Tip` (`components/ui/tip.tsx`)
   * — Radix's tooltip opens on FOCUS as well as hover, so that tooltip's own
   * `DismissableLayer` mounts and swallows the very next Escape keypress
   * before it ever reaches this dialog's `onEscapeKeyDown`, which reads as
   * "Escape does nothing" on a freshly-opened window. Omitted (the default
   * for every other caller) leaves Radix's own auto-focus untouched.
   */
  onOpenAutoFocus?: (event: Event) => void;
  /**
   * Opt-in, forwarded straight to `Dialog.Content`. Radix calls it once the
   * close animation has finished, so a caller whose close also unmounts the
   * dialog's own trigger (the What's new note) can wait for it instead of
   * cutting the animation off mid-flight. Omitted leaves Radix's default.
   */
  onCloseAutoFocus?: (event: Event) => void;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0"
          style={{
            background: 'rgba(20, 20, 26, 0.42)',
            backdropFilter: 'blur(10px) saturate(0.9)',
            WebkitBackdropFilter: 'blur(10px) saturate(0.9)',
          }}
        />
        <DialogPrimitive.Content
          data-testid={testId}
          onEscapeKeyDown={onEscapeKeyDown}
          onOpenAutoFocus={onOpenAutoFocus}
          onCloseAutoFocus={onCloseAutoFocus}
          className={cn(
            // `inset` + `m-auto` rather than the design-system dialog's
            // translate-based centring: the panel is sized by its own
            // max-height here, so centring it by transform would let a
            // short list sit off-centre the same way the chat window's
            // `items-start` bug did.
            'fixed inset-y-14 inset-x-6 z-50 m-auto flex w-auto flex-col overflow-hidden border border-border bg-card text-foreground outline-none data-open:animate-in data-open:fade-in-0 data-open:slide-in-from-bottom-2 data-closed:animate-out data-closed:fade-out-0',
            className,
          )}
          style={{
            maxWidth,
            borderRadius: 'var(--radius-xl-ds)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * The eyebrow bar: the EDDY mark, a quiet "EDDY · <context>" line, an
 * optional right-hand slot, and the close control. Same shape and spacing
 * as the chat window's own header.
 */
export function ZenDialogHeader({
  context,
  right,
  children,
}: {
  /** The words after "EDDY · " — e.g. "knowledge". */
  context: string;
  right?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex shrink-0 items-center gap-3 border-b border-border px-[22px] py-3.5">
      <EddyMark />
      <span className="min-w-0 truncate text-[length:var(--text-sm)] text-muted-foreground">
        EDDY · {context}
      </span>
      {children}
      <div className="flex-1" />
      {right}
      <DialogPrimitive.Close
        aria-label="Close"
        className="inline-flex size-8 items-center justify-center rounded-[10px] text-foreground transition-colors hover:bg-muted"
      >
        <X size={16} />
      </DialogPrimitive.Close>
    </div>
  );
}

/** The dialog's accessible name. Radix warns (and screen readers lose the
 *  dialog's identity) without one, so every ZenDialog must render it. */
export function ZenDialogTitle({ children, className }: { children: React.ReactNode; className?: string }) {
  return <DialogPrimitive.Title className={className}>{children}</DialogPrimitive.Title>;
}

/** The small caps line above a title — the chat window's own eyebrow, a
 *  short rule in the brand colour then the label. */
export function ZenEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2.5 text-[length:var(--text-xs)] font-medium uppercase tracking-[0.08em] text-muted-foreground">
      <span className="h-px w-5.5 bg-primary" style={{ width: 22 }} />
      <span className="font-semibold text-primary">{children}</span>
    </div>
  );
}
